// Time-only helpers (no date fields), shared by TimePicker/TimePickerField and the time
// side of DateTimePicker.

import {
  clampNumber,
  padDigits,
} from '@/components/ui/shadcn/custom/DateTimePicker/segmented-input-utils';

export type TimePrecision = 'hour' | 'minute' | 'second';
export type TimePopoverLayout =
  | 'columns'
  | 'single-column-list'
  | 'four-column-grid';

export function displayHour(hour24: number, use12Hour: boolean) {
  return use12Hour ? ((hour24 + 11) % 12) + 1 : hour24;
}

export function getPeriod(hour24: number): 'AM' | 'PM' {
  return hour24 >= 12 ? 'PM' : 'AM';
}

export function parseTime(value: string | undefined) {
  if (!value) return undefined;

  const match = value
    .trim()
    .match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return undefined;

  const hour = Number(match[1]);
  const minute = Number(match[2] ?? 0);
  const second = Number(match[3] ?? 0);
  const period = match[4]?.toUpperCase() as 'AM' | 'PM' | undefined;

  if (minute > 59 || second > 59) return undefined;
  if (period) {
    if (hour < 1 || hour > 12) return undefined;
    return {
      hour24: (hour % 12) + (period === 'PM' ? 12 : 0),
      minute,
      second,
    };
  }

  if (hour > 23) return undefined;
  return {
    hour24: hour,
    minute,
    second,
  };
}

export type TimeParts = {
  hour24?: number;
  minute?: number;
  second?: number;
  hour12?: number;
  period?: 'AM' | 'PM';
};

export type TimeFieldSegmentKey = 'hour' | 'minute' | 'second' | 'period';

export type TimePartsAction =
  | { type: 'set'; parts: TimeParts }
  | { type: 'clear' };

export function timePartsReducer(
  _state: TimeParts,
  action: TimePartsAction
): TimeParts {
  if (action.type === 'set') {
    return action.parts;
  }

  return {};
}

export function formatTime(
  parts: TimeParts,
  use12Hour: boolean,
  precision: TimePrecision
) {
  if (parts.hour24 === undefined) return '';

  const hour = padDigits(displayHour(parts.hour24, use12Hour), 2);
  const base =
    precision === 'hour'
      ? `${hour}:00`
      : precision === 'minute'
        ? `${hour}:${padDigits(parts.minute ?? 0, 2)}`
        : `${hour}:${padDigits(parts.minute ?? 0, 2)}:${padDigits(parts.second ?? 0, 2)}`;

  return use12Hour ? `${base} ${getPeriod(parts.hour24)}` : base;
}

export function getStepSeconds(step: number | string) {
  const parsed = Number(step);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

// A step in seconds >=60s that divides evenly into minutes drives the minute
// column's granularity; anything under 60s drives the second column's instead.
export function deriveMinuteSecondSteps(stepSeconds: number) {
  const minuteStep =
    stepSeconds >= 60 && stepSeconds % 60 === 0 ? stepSeconds / 60 : 1;
  const secondStep = stepSeconds < 60 ? stepSeconds : 1;
  return { minuteStep, secondStep };
}

export function buildHourOptions(use12Hour: boolean) {
  return use12Hour
    ? Array.from({ length: 12 }, (_, index) => index + 1)
    : Array.from({ length: 24 }, (_, index) => index);
}

// Builds the 0..<60 option list for the minute/second columns at a given step.
export function buildSixtyBasedOptions(step: number) {
  return Array.from(
    { length: Math.ceil(60 / step) },
    (_, index) => index * step
  ).filter((value) => value < 60);
}

export function getHour24WithPeriod(hour: number, period: 'AM' | 'PM') {
  const normalized = clampNumber(hour, 1, 12);
  return (normalized % 12) + (period === 'PM' ? 12 : 0);
}

export function isFourColumnHalfHourPairing(
  popoverLayout: TimePopoverLayout,
  precision: TimePrecision,
  timelineStepSeconds: number
) {
  return (
    popoverLayout === 'four-column-grid' &&
    precision === 'minute' &&
    timelineStepSeconds === 1800
  );
}

export function hasTimeValue(parts: TimeParts) {
  return (
    parts.hour24 !== undefined ||
    parts.minute !== undefined ||
    parts.second !== undefined ||
    parts.hour12 !== undefined ||
    parts.period !== undefined
  );
}

export function isTimeComplete(parts: TimeParts, precision: TimePrecision) {
  if (parts.hour24 === undefined) return false;
  if (precision === 'hour') return true;
  if (precision === 'minute') return parts.minute !== undefined;
  return parts.minute !== undefined && parts.second !== undefined;
}

export function segmentsFromTimeParts(
  parts: TimeParts,
  use12Hour: boolean,
  precision: TimePrecision
): Record<TimeFieldSegmentKey, string> {
  const displayHourValue =
    parts.hour24 !== undefined
      ? displayHour(parts.hour24, use12Hour)
      : parts.hour12;
  const periodValue =
    parts.hour24 !== undefined ? getPeriod(parts.hour24) : parts.period;

  return {
    hour: displayHourValue === undefined ? '' : padDigits(displayHourValue, 2),
    minute:
      parts.minute !== undefined
        ? padDigits(parts.minute, 2)
        : // Hour precision has no minute segment to fill in, so once the hour is set, show "00" instead of "--".
          precision === 'hour' && displayHourValue !== undefined
          ? '00'
          : '',
    second:
      precision === 'second' && parts.second !== undefined
        ? padDigits(parts.second, 2)
        : '',
    period: use12Hour ? (periodValue ?? '') : '',
  };
}

export function timePartsFromSegments(
  segments: Record<TimeFieldSegmentKey, string>,
  use12Hour: boolean,
  precision: TimePrecision
): TimeParts {
  const hour12 = segments.hour.length === 2 ? Number(segments.hour) : undefined;
  const period: 'AM' | 'PM' | undefined =
    use12Hour && segments.period ? (segments.period as 'AM' | 'PM') : undefined;
  const minute =
    segments.minute.length === 2 ? Number(segments.minute) : undefined;
  const second =
    precision === 'second' && segments.second.length === 2
      ? Number(segments.second)
      : undefined;

  let hour24: number | undefined;
  if (hour12 !== undefined) {
    hour24 = use12Hour
      ? period !== undefined
        ? getHour24WithPeriod(hour12, period)
        : undefined
      : hour12;
  }

  return {
    hour24,
    minute,
    second,
    hour12: use12Hour ? hour12 : undefined,
    period,
  };
}

export type TimeOption = {
  key: string;
  label: string;
  parts: TimeParts;
  period?: 'AM' | 'PM';
};

export function buildTimeOptionKey(
  hour24: number,
  minute: number,
  second: number
) {
  return `${padDigits(hour24, 2)}:${padDigits(minute, 2)}:${padDigits(second, 2)}`;
}

export function getTimelineStepSeconds(
  precision: TimePrecision,
  stepSeconds: number,
  minuteStep: number
) {
  if (precision === 'hour') return 3600;
  if (precision === 'minute') return minuteStep * 60;
  return stepSeconds;
}

export function buildDayOptions(
  precision: TimePrecision,
  use12Hour: boolean,
  timelineStepSeconds: number
) {
  const options: TimeOption[] = [];

  for (
    let totalSeconds = 0;
    totalSeconds < 24 * 60 * 60;
    totalSeconds += timelineStepSeconds
  ) {
    const hour24 = Math.floor(totalSeconds / 3600);
    const minute = Math.floor((totalSeconds % 3600) / 60);
    const second = totalSeconds % 60;

    const parts: TimeParts = {
      hour24,
      minute: precision === 'hour' ? 0 : minute,
      second: precision === 'second' ? second : 0,
    };

    options.push({
      key: buildTimeOptionKey(hour24, minute, second),
      label: formatTime(parts, use12Hour, precision),
      parts,
      period: use12Hour ? getPeriod(hour24) : undefined,
    });
  }

  return options;
}

// Builds 12 rows of [:00, :30, :00 (paired hour), :30 (paired hour)] options, either
// as AM/PM pairs (e.g. 7:00 AM / 7:00 PM) or 24-hour pairs (e.g. 07:00 / 19:00).
export function buildHalfHourPairRows(use12Hour: boolean): TimeOption[][] {
  const rows: TimeOption[][] = [];
  const baseHours = use12Hour
    ? [12, ...Array.from({ length: 11 }, (_, i) => i + 1)]
    : Array.from({ length: 12 }, (_, i) => i);

  const buildOption = (
    hour24: number,
    minute: number,
    labelHour: number,
    period: 'AM' | 'PM' | undefined
  ): TimeOption => ({
    key: buildTimeOptionKey(hour24, minute, 0),
    label: `${padDigits(labelHour, 2)}:${padDigits(minute, 2)}${period ? ` ${period}` : ''}`,
    parts: { hour24, minute, second: 0 },
    period,
  });

  for (const baseHour of baseHours) {
    const hour24First = use12Hour ? baseHour % 12 : baseHour;
    const hour24Second = hour24First + 12;
    const labelHourFirst = use12Hour ? baseHour : hour24First;
    const labelHourSecond = use12Hour ? baseHour : hour24Second;
    const periodFirst = use12Hour ? 'AM' : undefined;
    const periodSecond = use12Hour ? 'PM' : undefined;

    rows.push([
      buildOption(hour24First, 0, labelHourFirst, periodFirst),
      buildOption(hour24First, 30, labelHourFirst, periodFirst),
      buildOption(hour24Second, 0, labelHourSecond, periodSecond),
      buildOption(hour24Second, 30, labelHourSecond, periodSecond),
    ]);
  }

  return rows;
}

export function chunkOptions(options: TimeOption[], size: number) {
  const rows: TimeOption[][] = [];
  for (let index = 0; index < options.length; index += size) {
    rows.push(options.slice(index, index + size));
  }

  return rows;
}
